const dotWild = require('dot-wild')

const REFERENCE_FORMAT = /^\$(.+)\$$/

const operators = {
  $truthy(a, b) {
    return b ? !!a : !a
  },
  $falsy(a, b) {
    return b ? !a : !!a
  },
  $equals(a, b) {
    return a === b
  },
  $ne(a, b) {
    return a !== b
  },
  $in(a, b) {
    return b.includes(a)
  },
  $nin(a, b) {
    return !b.includes(a)
  },
  $includes(a, b) {
    return a.includes(b)
  },
  $gt(a, b) {
    return a > b
  },
  $lt(a, b) {
    return a < b
  },
  $gte(a, b) {
    return a >= b
  },
  $lte(a, b) {
    return a <= b
  },
  $ilike(a, b) {
    return new RegExp(b.replace(/%/g, '.*'), 'i').test(a)
  },

  $allEquals(as, b) {
    return as && as.every(a => this.$equals(a, b))
  },
  $allNe(as, b) {
    return as && as.every(a => this.$ne(a, b))
  },
  $allIn(as, b) {
    return as && as.every(a => this.$in(a, b))
  },
  $allNin(as, b) {
    return as && as.every(a => this.$nin(a, b))
  },
  $allIncludes(as, b) {
    return as && as.every(a => this.$includes(a, b))
  },
  $allGt(as, b) {
    return as && as.every(a => this.$gt(a, b))
  },
  $allLt(as, b) {
    return as && as.every(a => this.$lt(a, b))
  },
  $allGte(as, b) {
    return as && as.every(a => this.$gte(a, b))
  },
  $allLte(as, b) {
    return as && as.every(a => this.$lte(a, b))
  },
  $all(as, b) {
    return as && as.every(a => testConditions(b, a))
  },

  $anyEquals(as, b) {
    return as && as.some(a => this.$equals(a, b))
  },
  $anyNe(as, b) {
    return as && as.some(a => this.$ne(a, b))
  },
  $anyIn(as, b) {
    return as && as.some(a => this.$in(a, b))
  },
  $anyNin(as, b) {
    return as && as.some(a => this.$nin(a, b))
  },
  $anyIncludes(as, b) {
    return as && as.some(a => this.$includes(a, b))
  },
  $anyGt(as, b) {
    return as && as.some(a => this.$gt(a, b))
  },
  $anyLt(as, b) {
    return as && as.some(a => this.$lt(a, b))
  },
  $anyGte(as, b) {
    return as && as.some(a => this.$gte(a, b))
  },
  $anyLte(as, b) {
    return as && as.some(a => this.$lte(a, b))
  },
  $any(as, b) {
    return as && as.some(a => testConditions(b, a))
  },

  $lengthEquals(as, len) {
    return as && as.length === len
  },
  $lengthNe(as, len) {
    return as && as.length !== len
  },
  $lengthGt(as, len) {
    return as && as.length > len
  },
  $lengthLt(as, len) {
    return as && as.length < len
  },
  $lengthGte(as, len) {
    return as && as.length >= len
  },
  $lengthLte(as, len) {
    return as && as.length <= len
  },

  $or(conditions, data, options) {
    return conditions.reduce((acc, condition) => {
      return acc || testConditions(condition, data, options)
    }, false)
  },
  $and(conditions, data, options) {
    return conditions.reduce((acc, condition) => {
      return acc && testConditions(condition, data, options)
    }, true)
  },
  $not(conditions, data, options) {
    return !testConditions(conditions, data, options)
  },

  $match(data, regexp) {
    return data && data.match(regexp)
  },
}

function absolutePath(path, relativeTo) {
  if (path.startsWith('./') && relativeTo == null)
    throw new Error(`Relative paths cannot be used without 'relativeTo' option`)

  return path.startsWith('./') ? [relativeTo, path.substring(2)].filter(Boolean).join('.') : path
}

function isReference(value) {
  return REFERENCE_FORMAT.test(value)
}

function getReference(value) {
  return value.match(REFERENCE_FORMAT)[1]
}

function testConditions(conditions, data, { relativeTo = null } = {}) {
  return Object.entries(conditions).every(([key, condition]) => {
    if (key.startsWith('$') && ['$or', '$and', '$not'].includes(key)) {
      return operators[key](condition, data, { relativeTo })
    }

    return Object.entries(condition).every(([operator, value]) => {
      if (operators[operator]) {
        const a = dotWild.get(data, absolutePath(key, relativeTo))
        const b = isReference(value) ? dotWild.get(data, absolutePath(getReference(value), relativeTo)) : value

        return operators[operator](a, b)
      }
      throw new Error(`Unknown operator '${operator}'`)
    })
  })
}

module.exports = testConditions

const Conditions = {
  type: 'object',
  properties: {
    // scalar values
    $truthy: { type: 'boolean' },
    $falsy: { type: 'boolean' },
    $equals: { type: 'any' },
    $ne: { type: 'any' },
    $in: { type: 'any' },
    $nin: { type: 'any' },
    $includes: { type: 'any' },
    $gt: { type: 'any' },
    $lt: { type: 'any' },
    $gte: { type: 'any' },
    $lte: { type: 'any' },
    $ilike: { type: 'any' },
    $match: { type: 'any' },

    // array every value matches
    $allEquals: { type: 'any' },
    $allNe: { type: 'any' },
    $allIn: { type: 'any' },
    $allNin: { type: 'any' },
    $allIncludes: { type: 'any' },
    $allGt: { type: 'any' },
    $allLt: { type: 'any' },
    $allGte: { type: 'any' },
    $allLte: { type: 'any' },

    // array every value matches a condition
    $all: { type: 'object' },

    // array some value matches
    $anyEquals: { type: 'any' },
    $anyNe: { type: 'any' },
    $anyIn: { type: 'any' },
    $anyNin: { type: 'any' },
    $anyIncludes: { type: 'any' },
    $anyGt: { type: 'any' },
    $anyLt: { type: 'any' },
    $anyGte: { type: 'any' },
    $anyLte: { type: 'any' },

    // array some value matches a condition
    $any: { type: 'object' },

    // array length
    $lengthEquals: { type: 'integer' },
    $lengthNe: { type: 'integer' },
    $lengthGt: { type: 'integer' },
    $lengthLt: { type: 'integer' },
    $lengthGte: { type: 'integer' },
    $lengthLte: { type: 'integer' },
  },
  additionalProperties: false,
}

module.exports.conditionsSchema = {
  description: '[@dooer/test-conditions](https://github.com/Dooer/js-test-conditions) conditions',
  type: 'object',
  properties: {
    $or: {
      type: 'array',
      items: {
        type: 'object',
        patternProperties: {
          '.*': Conditions,
        },
      },
    },
    $and: {
      type: 'array',
      items: {
        type: 'object',
        patternProperties: {
          '.*': Conditions,
        },
      },
    },
  },
  patternProperties: {
    '^[^$].*': Conditions,
  },
}
